import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import {RegionState} from "@/interfaces/state/region";
import store from "@/store";
import {District, Province, Ward} from "@/interfaces/entity";
import {listCountries, listDistrictsByProvince, listProvinces, listWardsByDistrict} from "@/modules/regions/services";
import { IdMapper } from '@/interfaces/state/base';
import {Country} from "@/interfaces/entity/country";

@Module({dynamic: true, name: "region", store})
class RegionModule extends VuexModule implements RegionState {
    countryIds: number[] = [];
    countryIdMapper: IdMapper<Country> = {};
    provinceIds: number[] = [];
    provinceIdMapper: IdMapper<Province> = {};
    districtIds: number[] = [];
    districtIdMapper: IdMapper<District> = {};
    wardIds: number[] = [];
    wardIdMapper: IdMapper<Ward> = {};


    @Mutation
    private ADD_PROVINCE(payload: Province) {
        if (!payload || this.provinceIds.includes(payload.id)) return;
        this.provinceIds = [...this.provinceIds, payload.id];
        this.provinceIdMapper = {...this.provinceIdMapper, [payload.id]: payload};
    }

    @Mutation
    private ADD_DISTRICT(payload: District) {
        if (!payload || this.districtIds.includes(payload.id)) return;
        this.districtIds = [...this.districtIds, payload.id];
        this.districtIdMapper = {...this.districtIdMapper, [payload.id]: payload};
    }

    @Mutation
    private ADD_WARD(payload: Ward) {
        if (!payload || this.wardIds.includes(payload.id)) return;
        this.wardIds = [...this.wardIds, payload.id];
        this.wardIdMapper = {...this.wardIdMapper, [payload.id]: payload};
    }

    @Mutation
    private MAP_COUNTRY(payload: Country[]) {
        const data: IdMapper<Country> = {};
        this.countryIds = payload.map((country: Country) => {
            data[country.id] = country;
            return country.id;
        });
        this.countryIdMapper = data;
    }

    @Mutation
    private MAP_PROVINCES(payload: Province[]) {
        const data: IdMapper<Province> = {};
        this.provinceIds = payload.map((province: Province) => {
            data[province.id] = province;
            return province.id;
        });
        this.provinceIdMapper = data;
    }

    @Mutation
    private MAP_DISTRICTS(payload: District[]) {
        const data: IdMapper<District> = {};
        this.districtIds = payload.map((district: District) => {
            data[district.id] = district;
            return district.id;
        });
        this.districtIdMapper = data;
    }

    @Mutation
    private MAP_WARDS(payload: Ward[]) {
        const data: IdMapper<Ward> = {};
        this.wardIds = payload.map((ward: Ward) => {
            data[ward.id] = ward;
            return ward.id;
        });
        this.wardIdMapper = data;
    }

    @Action({ rawError: true })
    public addProvince(payload: Province) {
        this.ADD_PROVINCE(payload);
    }

    @Action({ rawError: true })
    public addDistrict(payload: District) {
        this.ADD_DISTRICT(payload);
    }

    @Action({ rawError: true })
    public addWard(payload: Ward) {
        this.ADD_WARD(payload);
    }

    @Action
    public async getCountries() {
        const { data } = await listCountries();
        this.MAP_COUNTRY(data?.datas);
        return data.datas;
    }

    @Action
    public async getProvinces(countryId = 1) {
        const { data } = await listProvinces(countryId);
        this.MAP_PROVINCES(data?.datas);
        return data.datas;
    }


    @Action
    public async getDistricts(provinceId: number) {
        const { data } = await listDistrictsByProvince(provinceId);
        this.MAP_DISTRICTS(data?.datas);
        return data.datas;
    }

    @Action
    public async getWards(districtId: number) {
        const { data } = await listWardsByDistrict(districtId);
        this.MAP_WARDS(data?.datas);
        return data.datas;
    }

    @Action
    public clearDistricts() {
        this.MAP_DISTRICTS([]);
    }

    @Action
    public clearWards() {
        this.MAP_WARDS([]);
    }
}

export const RegionStore = getModule(RegionModule);
